import * as React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';

import CampaignPayoutsListItems from 'modules/campaigns/components/payments/payouts/list/items/CampaignPayoutsListItems';
import CampaignPayoutsListSummary from 'modules/campaigns/components/payments/payouts/list/summary/CampaignPayoutsListSummary';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { unreleasePartialPayout } from 'modules/campaigns/reducers/payouts/campaign-payouts';
import { fetchPaymentInfo } from '../../../redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import {
  getPayoutSummary,
  preparePayoutsForRendering
} from '../../../utils/payouts';
import { canReleasePayout as canReleasePayoutUtil } from '../../../utils/general';

import { PaymentConfirmationModalParams, PayoutWithHyphenAmount, Subcampaign } from 'types';
import { CcmCampaignPaymentsState } from '../../../redux/types/CcmCampaignPaymentsRedux.type';
import { PayoutsSummary } from '../../../types/PayoutsSummary.type';

applyTranslations(require('./ccm-campaign-payments-payouts-list.i18n.json'));

interface ParttialGLobalState {
  ccmCampaignPayments: CcmCampaignPaymentsState
}

interface OwnProps {
  campaignId: number,
  subcampaign: Subcampaign
}

interface StateProps {
  payouts: Array<PayoutWithHyphenAmount>,
  summary: PayoutsSummary,
  canReleasePayout: (p: PayoutWithHyphenAmount) => boolean
}

interface DispatchProps {
  dispatchHideConfirmModal: () => void,
  dispatchShowConfirmModal: (p: PaymentConfirmationModalParams) => void,
  dispatchUnreleasePartialPayout: (payoutId: number, campaignId: number, subcampaignId: number) => void,
  dispatchOnPayoutRelease: () => void
}

type Props = OwnProps & StateProps & DispatchProps;

const CcmCampaignPaymentsPayoutsList = ({
  campaignId,
  subcampaign,
  payouts,
  summary,
  canReleasePayout,
  dispatchUnreleasePartialPayout,
  dispatchOnPayoutRelease,
  dispatchHideConfirmModal,
  dispatchShowConfirmModal
}: Props) => (
  <div>
    <CampaignPayoutsListSummary
      data={{
        total: {
          label: __t('ccm_campaign_payments_payouts_list.labels.total'),
          total: typeof summary.total === 'string' ? summary.total : renderPrice(summary.total)
        },
        released: {
          label: __t('ccm_campaign_payments_payouts_list.labels.released'),
          total: typeof summary.released === 'string' ? summary.released : renderPrice(summary.released)
        },
        remaining: {
          label: __t('ccm_campaign_payments_payouts_list.labels.remaining'),
          total: typeof summary.remaining === 'string' ? summary.remaining : renderPrice(summary.remaining)
        }
      }}
    />
    <CampaignPayoutsListItems
      campaignId={campaignId}
      headerItems={[
        __t('ccm_campaign_payments_payouts_list.labels.note'),
        __t('ccm_campaign_payments_payouts_list.labels.amount'),
        __t('ccm_campaign_payments_payouts_list.labels.date_scheduled'),
        __t('ccm_campaign_payments_payouts_list.labels.date_released'),
        __t('ccm_campaign_payments_payouts_list.labels.payment_expected'),
        __t('ccm_campaign_payments_payouts_list.labels.status')
      ]}
      listItems={payouts}
      subcampaign={subcampaign}
      isBrand
      canReleasePayout={canReleasePayout}
      onPayoutRelease={dispatchOnPayoutRelease}
      onUnreleasePartialPayout={dispatchUnreleasePartialPayout}
      onHideConfirmationModal={dispatchHideConfirmModal}
      onShowConfirmationModal={dispatchShowConfirmModal}
    />
  </div>
);

const mapStateToProps = (
  { ccmCampaignPayments }: ParttialGLobalState,
  { subcampaign }: OwnProps
): StateProps => {
  const payouts = ccmCampaignPayments.creatorPayouts.list.filter(p => p.subcampaignId === subcampaign.id);

  return {
    canReleasePayout: payout => canReleasePayoutUtil(
      payout,
      subcampaign?.campaign.endDate ?? null,
      moment()
    ),
    payouts: preparePayoutsForRendering(
      payouts,
      subcampaign?.campaign?.timeline ?? null,
      moment()
    ),
    summary: getPayoutSummary(payouts)
  };
};

const mapDispatchToProps = (dispatch, { subcampaign }: OwnProps): DispatchProps => ({
  dispatchOnPayoutRelease: () => dispatch(fetchPaymentInfo(subcampaign.campaignId)),
  dispatchUnreleasePartialPayout: (payoutId, campaignId, subcampaignId) => (
    dispatch(unreleasePartialPayout(
      payoutId,
      campaignId,
      subcampaignId,
      () => dispatch(fetchPaymentInfo(campaignId))))
  ),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal()),
  dispatchShowConfirmModal: params => dispatch(showConfirmModal(params))
});

export default connect(mapStateToProps, mapDispatchToProps)(CcmCampaignPaymentsPayoutsList);
